import { __awaiter, __generator } from "tslib";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { FileNotFoundError } from "../../core/io/Types";
import { ZipDocumentContainer } from "../../core/io/ZipDocumentContainer";
import { BasicElement } from "../../core/mdl/BasicElement";
import { CameraMode } from "../../core/view/CameraControl";
import * as basket from "../../eaiws/basket";
import { Log, Progress } from "../../utils";
import { addElements } from "../../utils/array";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, newError } from "../../utils/error";
import { isNotNullOrEmpty, isNullOrEmpty } from "../../utils/string";
import { ArticleImport, MainArticleElement, UserArticleElement } from "../main";
import { GfjLoader } from "./GfjLoader";
import { ObxReader } from "./ObxReader";
export function importPec(pArticleManager, pPecData, pOptions) {
    if (pOptions === void 0) { pOptions = {}; }
    return __awaiter(this, void 0, void 0, function () {
        var tContainer;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (pPecData == null)
                        throw new Error(ERR_INVALID_ARGUMENT);
                    return [4 /*yield*/, ZipDocumentContainer.LoadContainer(pPecData)];
                case 1:
                    tContainer = _a.sent();
                    return [2 /*return*/, (importPecFromContainer(pArticleManager, tContainer, pOptions))];
            }
        });
    });
}
export function importPecFromContainer(pArticleManager, pContainer, pOptions) {
    if (pOptions === void 0) { pOptions = {}; }
    var tImport = new PecImport(pArticleManager);
    return (tImport.importFromContainer(pContainer, pOptions));
}
export function importPecFromCatalog(pArticleManager, pCatalogContainerItem, pOptions) {
    if (pOptions === void 0) { pOptions = {}; }
    var tImport = new PecImport(pArticleManager);
    return (tImport.importFromCatalog(pCatalogContainerItem, pOptions));
}
var PecImport = /** @class */ (function () {
    function PecImport(pArticleManager) {
        this.mArticleManager = pArticleManager;
    }
    PecImport.prototype.importFromContainer = function (pContainer, pOptions) {
        if (pOptions === void 0) { pOptions = {}; }
        return __awaiter(this, void 0, void 0, function () {
            var tContext;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (pContainer == null)
                            throw new Error(ERR_INVALID_ARGUMENT);
                        tContext = {};
                        tContext.itemProps = null;
                        tContext.loadedElements = new Array();
                        tContext.progress = new Progress(pOptions.onProgress, true);
                        tContext.gfjLoader = new GfjLoader(this.mArticleManager.app.model);
                        tContext.gfjLoader.onResolveUrl = function (pUrl) { return _this.resolveGfjUrl(pUrl, tContext); };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 6, 7]);
                        this.mArticleManager.app.viewer.beginDisableRendering();
                        tContext.progress.startSection(5);
                        tContext.container = pContainer;
                        return [4 /*yield*/, this.readPecXml(tContext, pOptions.view)];
                    case 2:
                        _a.sent();
                        tContext.progress.startSection(5);
                        return [4 /*yield*/, this.importObx(tContext, pOptions.dataLanguage)];
                    case 3:
                        _a.sent();
                        tContext.progress.startSection(60);
                        return [4 /*yield*/, this.loadArticles(tContext, pOptions.shouldUpdateCallback)];
                    case 4:
                        _a.sent();
                        tContext.progress.startSection(30);
                        return [4 /*yield*/, this.loadScene(tContext)];
                    case 5:
                        _a.sent();
                        tContext.progress.setProgress(100);
                        return [3 /*break*/, 7];
                    case 6:
                        this.mArticleManager.app.viewer.requestRenderFrame();
                        this.mArticleManager.app.viewer.endDisableRendering();
                        return [7 /*endfinally*/];
                    case 7: return [2 /*return*/, (tContext.loadedElements)];
                }
            });
        });
    };
    PecImport.prototype.importFromCatalog = function (pContainerItem, pOptions) {
        if (pOptions === void 0) { pOptions = {}; }
        return __awaiter(this, void 0, void 0, function () {
            var tContext;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (pContainerItem == null)
                            throw new Error(ERR_INVALID_ARGUMENT);
                        tContext = {};
                        tContext.itemProps = null;
                        tContext.loadedElements = new Array();
                        tContext.progress = new Progress(pOptions.onProgress, true);
                        tContext.gfjLoader = new GfjLoader(this.mArticleManager.app.model);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 5, 6]);
                        this.mArticleManager.app.viewer.beginDisableRendering();
                        tContext.progress.startSection(10);
                        return [4 /*yield*/, this.pasteContainerItem(tContext, pContainerItem)];
                    case 2:
                        _a.sent();
                        tContext.progress.startSection(60);
                        return [4 /*yield*/, this.loadArticles(tContext, pOptions.shouldUpdateCallback)];
                    case 3:
                        _a.sent();
                        tContext.progress.startSection(30);
                        return [4 /*yield*/, this.loadScene(tContext)];
                    case 4:
                        _a.sent();
                        tContext.progress.setProgress(100);
                        return [3 /*break*/, 6];
                    case 5:
                        this.mArticleManager.app.viewer.requestRenderFrame();
                        this.mArticleManager.app.viewer.endDisableRendering();
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/, (tContext.loadedElements)];
                }
            });
        });
    };
    PecImport.prototype.readPecXml = function (pContext, pView) {
        return __awaiter(this, void 0, void 0, function () {
            var tXmlString, tParser, tDoc, tRoot, tResourcesList, i, tResourceElement, tPathElement, tPath, tIsGfj, tUri, tGeomCkSum, tSceneElement, tUri;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (pContext.container == null)
                            throw newError(ERR_INVALID_STATE, "container is null");
                        return [4 /*yield*/, pContext.container.getFile("pec.xml", false)];
                    case 1:
                        tXmlString = _a.sent();
                        tParser = new DOMParser();
                        tDoc = tParser.parseFromString(tXmlString, "application/xml");
                        if (tDoc == null || tDoc.documentElement == null)
                            throw new Error("Failed to read pec.xml.");
                        tRoot = tDoc.documentElement;
                        if (tRoot.tagName !== "pec")
                            throw new Error("Invalid root element in pec.xml.");
                        //get resource path mapping
                        pContext.uriResourcePathMap = new Map();
                        pContext.gfjResourcePathMap = new Map();
                        tResourcesList = tRoot.querySelectorAll("resources > resource");
                        if (tResourcesList != null) {
                            for (i = 0; i < tResourcesList.length; ++i) {
                                tResourceElement = tResourcesList.item(i);
                                tPathElement = tResourceElement.querySelector("path");
                                if (tPathElement != null) {
                                    tPath = tPathElement.textContent;
                                    if (!isNullOrEmpty(tPath)) {
                                        tIsGfj = tPath.endsWith(".gfj");
                                        tUri = tResourceElement.getAttribute("uri");
                                        if (!isNullOrEmpty(tUri)) {
                                            pContext.uriResourcePathMap.set(tUri, tPath);
                                            if (tIsGfj)
                                                pContext.gfjResourcePathMap.set(tUri, tPath);
                                        }
                                        tGeomCkSum = tResourceElement.getAttribute("geometryChecksum");
                                        if (!isNullOrEmpty(tGeomCkSum) && tIsGfj)
                                            pContext.gfjResourcePathMap.set(tGeomCkSum, tPath);
                                    }
                                }
                            }
                        }
                        tSceneElement = tRoot.querySelector("scene");
                        if (tSceneElement != null) {
                            tUri = tSceneElement.getAttribute("uri");
                            if (isNotNullOrEmpty(tUri))
                                pContext.sceneGfjUrl = pContext.gfjResourcePathMap.get(tUri);
                        }
                        //load camera
                        if (pView != null)
                            this.loadCamera(pView, tRoot);
                        return [2 /*return*/];
                }
            });
        });
    };
    PecImport.prototype.loadCamera = function (pView, pPecXmlRoot) {
        var tCamElement = pPecXmlRoot.querySelector("camera");
        if (tCamElement == null)
            return;
        //mode
        var tType = tCamElement.getAttribute("type");
        if (tType === "orthographic") {
            pView.cameraControl.cameraMode = CameraMode.Orthographic;
            //ortho Y
            var tOrthoElement = tCamElement.querySelector("orthoY");
            if (tOrthoElement != null) {
                pView.cameraControl.setCameraOrtho(parseFloat(tOrthoElement.getAttribute("value")));
            }
        }
        else {
            pView.cameraControl.cameraMode = CameraMode.Perspective;
        }
        //position
        var tPosElement = tCamElement.querySelector("position");
        if (tPosElement != null) {
            var tPosition = Vector3.Zero();
            tPosition.x = parseFloat(tPosElement.getAttribute("x"));
            tPosition.y = parseFloat(tPosElement.getAttribute("y"));
            tPosition.z = -parseFloat(tPosElement.getAttribute("z"));
            pView.cameraControl.setPosition(tPosition);
        }
        //direction
        var tDirElement = tCamElement.querySelector("direction");
        if (tDirElement != null) {
            var tDir = Vector3.Zero();
            tDir.x = parseFloat(tDirElement.getAttribute("x"));
            tDir.y = parseFloat(tDirElement.getAttribute("y"));
            tDir.z = -parseFloat(tDirElement.getAttribute("z"));
            pView.cameraControl.setDirection(tDir);
        }
    };
    PecImport.prototype.importObx = function (pContext, pDataLanguage) {
        return __awaiter(this, void 0, void 0, function () {
            var tObxData, tObxBlob, tSession, tUploadUrl, tPastedIds, tOptions, tPropsOptions, _a, _b, tObxReader, pError_1;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 10, , 11]);
                        if (pContext.container == null)
                            throw newError(ERR_INVALID_STATE, "container is null");
                        return [4 /*yield*/, pContext.container.getFile("document.obx", true)];
                    case 1:
                        tObxData = _c.sent();
                        tObxBlob = new Blob([tObxData]);
                        tSession = this.mArticleManager.session;
                        if (!tSession.isValid) return [3 /*break*/, 7];
                        return [4 /*yield*/, tSession.uploadFile("CutBuffer", tObxBlob)];
                    case 2:
                        tUploadUrl = _c.sent();
                        if (isNullOrEmpty(tUploadUrl))
                            throw new Error("Failed to upload obx.");
                        return [4 /*yield*/, tSession.basket.paste(this.mArticleManager.fatherItemId, null, tUploadUrl)];
                    case 3:
                        tPastedIds = _c.sent();
                        if (tPastedIds == null || tPastedIds.length === 0)
                            return [2 /*return*/];
                        //delete uploaded obx
                        return [4 /*yield*/, tSession.deleteFile(tUploadUrl)];
                    case 4:
                        //delete uploaded obx
                        _c.sent();
                        tOptions = new basket.GetAllItemsOptions();
                        tOptions.subItems = true;
                        tOptions.wholeComposite = true;
                        tOptions.geometryIds = true;
                        tOptions.setArticleIds = true;
                        tPropsOptions = this.mArticleManager._GetDefaultItemPropsOptions();
                        tPropsOptions.subItems = true;
                        tPropsOptions.wholeComposite = true;
                        _a = pContext;
                        return [4 /*yield*/, tSession.basket.getAllItems(tPastedIds, tOptions)];
                    case 5:
                        _a.items = _c.sent();
                        _b = pContext;
                        return [4 /*yield*/, tSession.basket.getItemProperties(tPastedIds, tPropsOptions)];
                    case 6:
                        _b.itemProps = _c.sent();
                        return [3 /*break*/, 9];
                    case 7:
                        tObxReader = new ObxReader();
                        if (pDataLanguage != null) {
                            tObxReader.setLanguages(pDataLanguage);
                        }
                        return [4 /*yield*/, tObxReader._loadObx(tObxBlob)];
                    case 8:
                        _c.sent();
                        pContext.items = tObxReader.getAllItems();
                        pContext.itemProps = tObxReader.getAllItemProperties();
                        _c.label = 9;
                    case 9: return [3 /*break*/, 11];
                    case 10:
                        pError_1 = _c.sent();
                        //document.obx is optional
                        if (!(pError_1 instanceof FileNotFoundError))
                            throw (pError_1);
                        return [3 /*break*/, 11];
                    case 11: return [2 /*return*/];
                }
            });
        });
    };
    PecImport.prototype.pasteContainerItem = function (pContext, pContainerItem) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var tUri, _i, _b, tRes, tSession, tPasteOptions, tPasteResult, tPastedIds, tGeomUris, i, tGeomUri, tResolvedUrls, i, tPropsOptions, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (pContainerItem.type !== "Container")
                            throw new Error("Invalid catalog item type.");
                        tUri = null;
                        if (pContainerItem.resources != null) {
                            for (_i = 0, _b = pContainerItem.resources; _i < _b.length; _i++) {
                                tRes = _b[_i];
                                if (tRes.type === "XCF:PEC") {
                                    tUri = (_a = tRes.url) !== null && _a !== void 0 ? _a : tRes.value;
                                    break;
                                }
                            }
                        }
                        if (isNullOrEmpty(tUri))
                            throw new Error("Catalog item does not have XCF:PEC resource.");
                        tSession = this.mArticleManager.session;
                        tPasteOptions = new basket.PasteContainerOptions();
                        tPasteOptions.setArticleIds = true;
                        tPasteOptions.geometryIds = true;
                        return [4 /*yield*/, tSession.basket.pasteContainer(null, null, tUri, tPasteOptions)];
                    case 1:
                        tPasteResult = _d.sent();
                        if (tPasteResult == null)
                            return [2 /*return*/];
                        tPastedIds = new Array();
                        tGeomUris = new Array();
                        if (tPasteResult.items != null && tPasteResult.itemGeometries != null) {
                            for (i = 0; i < tPasteResult.items.length; ++i) {
                                tPastedIds.push(tPasteResult.items[i].itemId);
                                tGeomUri = tPasteResult.itemGeometries[i];
                                if (isNullOrEmpty(tGeomUri)) {
                                    //add dummy url to prevent invalid url exception of resolveURIs
                                    tGeomUris.push("imp:00000000000000000000000000000000");
                                }
                                else
                                    tGeomUris.push(tGeomUri);
                            }
                        }
                        //resolve uris (including sceneGeometry)
                        pContext.basketIdGfjMap = new Map();
                        if (!isNullOrEmpty(tPasteResult.sceneGeometry))
                            tGeomUris.push(tPasteResult.sceneGeometry);
                        return [4 /*yield*/, tSession.session.resolveURIs(tGeomUris)];
                    case 2:
                        tResolvedUrls = _d.sent();
                        for (i = 0; i < tPastedIds.length; ++i) {
                            pContext.basketIdGfjMap.set(tPastedIds[i], tResolvedUrls[i]);
                        }
                        if (!isNullOrEmpty(tPasteResult.sceneGeometry))
                            pContext.sceneGfjUrl = tResolvedUrls[tResolvedUrls.length - 1];
                        tPropsOptions = this.mArticleManager._GetDefaultItemPropsOptions();
                        tPropsOptions.subItems = true;
                        tPropsOptions.wholeComposite = true;
                        pContext.items = tPasteResult.items;
                        _c = pContext;
                        return [4 /*yield*/, tSession.basket.getItemProperties(tPastedIds, tPropsOptions)];
                    case 3:
                        _c.itemProps = _d.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    PecImport.prototype.loadArticles = function (pContext, pShouldUpdateCallback) {
        return __awaiter(this, void 0, void 0, function () {
            var tArticles, tMainArticles, _i, _a, tProps, _b, _c, tProps, _d, _e, tItem, tStep, _f, tMainArticles_1, tMainArticle, tArticle, pError_2;
            return __generator(this, function (_g) {
                switch (_g.label) {
                    case 0:
                        if (pContext.itemProps == null || pContext.itemProps.length === 0 ||
                            pContext.items == null || pContext.items.length === 0)
                            return [2 /*return*/];
                        if (!(pContext.gfjResourcePathMap != null && pContext.gfjResourcePathMap.size === 0)) return [3 /*break*/, 2];
                        return [4 /*yield*/, ArticleImport.Import(this.mArticleManager, pContext.items, pContext.itemProps, false, { shouldUpdateCallback: pShouldUpdateCallback })];
                    case 1:
                        tArticles = _g.sent();
                        if (tArticles != null) {
                            addElements(pContext.loadedElements, tArticles);
                        }
                        return [2 /*return*/];
                    case 2:
                        tMainArticles = new Array();
                        for (_i = 0, _a = pContext.itemProps; _i < _a.length; _i++) {
                            tProps = _a[_i];
                            if (tProps.article != null && tProps.composableGeometry != null && tProps.composableGeometry.enabled) {
                                tMainArticles.push(tProps);
                            }
                        }
                        if (tMainArticles.length === 0)
                            return [2 /*return*/];
                        //build map from basket id to properties
                        pContext.itemPropsMap = new Map();
                        for (_b = 0, _c = pContext.itemProps; _b < _c.length; _b++) {
                            tProps = _c[_b];
                            pContext.itemPropsMap.set(tProps.itemId, tProps);
                        }
                        //build map from basket id to item
                        pContext.itemsMap = new Map();
                        for (_d = 0, _e = pContext.items; _d < _e.length; _d++) {
                            tItem = _e[_d];
                            pContext.itemsMap.set(tItem.itemId, tItem);
                        }
                        tStep = 100 / tMainArticles.length;
                        _f = 0, tMainArticles_1 = tMainArticles;
                        _g.label = 3;
                    case 3:
                        if (!(_f < tMainArticles_1.length)) return [3 /*break*/, 9];
                        tMainArticle = tMainArticles_1[_f];
                        _g.label = 4;
                    case 4:
                        _g.trys.push([4, 6, , 7]);
                        return [4 /*yield*/, this.loadArticle(tMainArticle, pContext)];
                    case 5:
                        tArticle = _g.sent();
                        if (tArticle != null)
                            pContext.loadedElements.push(tArticle);
                        return [3 /*break*/, 7];
                    case 6:
                        pError_2 = _g.sent();
                        Log.error("Failed to load article " + tMainArticle.itemId, pError_2);
                        return [3 /*break*/, 7];
                    case 7:
                        pContext.progress.setProgress(pContext.progress.sectionProgress + tStep);
                        _g.label = 8;
                    case 8:
                        _f++;
                        return [3 /*break*/, 3];
                    case 9: return [2 /*return*/];
                }
            });
        });
    };
    PecImport.prototype.loadArticle = function (pMainArticle, pContext) {
        return __awaiter(this, void 0, void 0, function () {
            var tSession, tGfjPath, tArticleItems, tNodes, tNode, tArticle, tItemProperties, _i, tArticleItems_1, tItem, tProps;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        tSession = this.mArticleManager.session;
                        tGfjPath = this.getArticleGfjPath(pMainArticle, pContext);
                        if (isNullOrEmpty(tGfjPath))
                            throw new Error("gfj path not found");
                        tArticleItems = this.mArticleManager._getCompositeItems(pMainArticle.itemId, pContext.itemsMap);
                        return [4 /*yield*/, pContext.gfjLoader.load(tGfjPath, pMainArticle.itemId, tArticleItems, pContext.container)];
                    case 1:
                        tNodes = _a.sent();
                        tNode = null;
                        if (tNodes != null && tNodes.length > 0) {
                            tNode = tNodes[0];
                        }
                        tArticle = null;
                        if (pMainArticle.itemType === "UserArticle") {
                            tArticle = new UserArticleElement(pMainArticle.itemId, this.mArticleManager, tNode);
                        }
                        else {
                            tArticle = new MainArticleElement(pMainArticle.itemId, this.mArticleManager, tNode);
                        }
                        tItemProperties = new Array();
                        for (_i = 0, tArticleItems_1 = tArticleItems; _i < tArticleItems_1.length; _i++) {
                            tItem = tArticleItems_1[_i];
                            tProps = pContext.itemPropsMap.get(tItem.itemId);
                            if (tProps != null)
                                tItemProperties.push(tProps);
                        }
                        tArticle.fillItemPropertiesCache(tItemProperties);
                        if (!(tSession.isValid && tArticle instanceof UserArticleElement)) return [3 /*break*/, 3];
                        return [4 /*yield*/, tArticle._resetImpGeometry()];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3:
                        if (!(tArticle.node == null)) return [3 /*break*/, 5];
                        return [4 /*yield*/, tArticle._initPlaceholderGeometry(tArticleItems)];
                    case 4:
                        _a.sent();
                        _a.label = 5;
                    case 5:
                        //load transformation
                        tArticle._loadEaiwsTransform(pMainArticle.composableGeometry);
                        return [2 /*return*/, (tArticle)];
                }
            });
        });
    };
    PecImport.prototype.getArticleGfjPath = function (pMainArticle, pContext) {
        if (pContext.basketIdGfjMap != null) {
            return (pContext.basketIdGfjMap.get(pMainArticle.itemId));
        }
        if (pContext.gfjResourcePathMap != null) {
            if (pMainArticle.itemType === "UserArticle") {
                //use geometry for path mapping
                var tGeometry = pMainArticle.composableGeometry.geometry;
                if (tGeometry == null)
                    throw new Error("missing geometry for user article");
                return (pContext.gfjResourcePathMap.get(tGeometry));
            }
            else {
                //use geometryId for path mapping
                var tGeomId = this.normalizeBase64(pMainArticle.article.geometryId);
                if (isNullOrEmpty(tGeomId))
                    throw new Error("missing geometryId");
                return (pContext.gfjResourcePathMap.get(tGeomId));
            }
        }
        return (null);
    };
    PecImport.prototype.loadScene = function (pContext) {
        return __awaiter(this, void 0, void 0, function () {
            var tResults, _i, tResults_1, tResult, tElement, pError_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (isNullOrEmpty(pContext.sceneGfjUrl))
                            return [2 /*return*/];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, pContext.gfjLoader.load(pContext.sceneGfjUrl, null, null, pContext.container)];
                    case 2:
                        tResults = _a.sent();
                        if (tResults != null && tResults.length > 0) {
                            for (_i = 0, tResults_1 = tResults; _i < tResults_1.length; _i++) {
                                tResult = tResults_1[_i];
                                if (tResult != null) {
                                    tElement = new BasicElement(this.mArticleManager.app.model, tResult);
                                    pContext.loadedElements.push(tElement);
                                }
                            }
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        pError_3 = _a.sent();
                        //scene.gfj is optional
                        if (!(pError_3 instanceof FileNotFoundError))
                            throw (pError_3);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    PecImport.prototype.resolveGfjUrl = function (pUrl, pContext) {
        if (pUrl == null)
            return ("");
        if (pContext.uriResourcePathMap != null && pUrl.startsWith("imp:")) {
            var tUrl = pContext.uriResourcePathMap.get(pUrl);
            return (tUrl != null ? tUrl : "");
        }
        return (pUrl);
    };
    PecImport.prototype.normalizeBase64 = function (pBase64) {
        if (pBase64 == null)
            return (null);
        var tBase64 = pBase64;
        tBase64 = tBase64.replace(/_/g, "/");
        tBase64 = tBase64.replace(/-/g, "+");
        return (tBase64);
    };
    return PecImport;
}());
